import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useInventory } from '../contexts/InventoryContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { useBarcodeHistory } from '../hooks/useBarcodeHistory';
import { usePhomemoSettings } from '../hooks/usePhomemoSettings';
import { supabase } from '../lib/supabase';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LocationPicker, type BinDetails } from '../components/warehouse/LocationPicker';
import {
  QrCode, Package, Download, Printer, RefreshCw, Barcode, Search,
  Check, X, History, LayoutTemplate, PlusCircle, Layers,
  TrendingUp, Filter, RotateCcw, Trash2,
  ChevronLeft, ChevronRight, Wand2, MapPin, FileSpreadsheet,
  ChevronUp, ChevronDown, Tag, Radio
} from 'lucide-react';
import {
  type BarcodeType,
  type CodeFormat,
  BARCODE_FORMATS,
  generateBarcode,
  validateBarcode,
  drawBarcodeToCanvas,
  getBarcodeDataURL
} from '../utils/barcodeFormats';
import {
  type LabelTemplate,
  type LabelData,
  generateSingleLabelPrintHTML,
  generateBulkLabelsPrintHTML,
  formatLocationForLabel,
  openPrintWindow,
  openPrintWindowOrPhomemo
} from '../utils/labelGenerator';
import { getProductsBinLocations } from '../lib/services/binInventoryService';
import { formatBinLocationCompact } from '../utils/binLocationFormat';
import type { ProductBinLocation } from '../types/warehouse';
import { getAdminId } from '../utils/getAdminId';
import { RfidIdentifierTab } from '../components/rfid/rfid-identifier-tab';
import { RfidEncoderTab } from '../components/rfid/encoder/rfid-encoder-tab';

type TabType = 'generate' | 'bulk' | 'templates' | 'history' | 'rfid' | 'rfid-encoder';

const DEFAULT_LABEL_OPTS = {
  locationPosition: 'below' as const,
  textStyle: 'normal' as const,
  textColor: '#111111',
};

const DEFAULT_TEMPLATES: LabelTemplate[] = [
  { id: 'small', name: 'Small (1" x 0.5")', width: 1, height: 0.5, unit: 'in', showName: true, showPrice: false, showSku: false, showDate: false, showLocation: false, showLogo: false, fontSize: 'small', orientation: 'landscape', ...DEFAULT_LABEL_OPTS },
  { id: 'standard', name: 'Standard (2" x 1")', width: 2, height: 1, unit: 'in', showName: true, showPrice: true, showSku: true, showDate: false, showLocation: false, showLogo: false, fontSize: 'medium', orientation: 'landscape', ...DEFAULT_LABEL_OPTS },
  { id: 'large', name: 'Large (4" x 2")', width: 4, height: 2, unit: 'in', showName: true, showPrice: true, showSku: true, showDate: true, showLocation: true, showLogo: true, fontSize: 'large', orientation: 'landscape', ...DEFAULT_LABEL_OPTS },
  { id: 'shelf', name: 'Shelf Label (2" x 1.25")', width: 2, height: 1.25, unit: 'in', showName: true, showPrice: true, showSku: false, showDate: false, showLocation: false, showLogo: false, fontSize: 'medium', orientation: 'landscape', ...DEFAULT_LABEL_OPTS }
];

type BarcodeFilter = 'all' | 'has_barcode' | 'no_barcode';

const ITEMS_PER_PAGE = 500;

export const BarcodeGenerator: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { addItem, fetchAllItems, allItems, allItemsLoading, updateMultipleItems } = useInventory();
  const { history, stats, addToHistory, incrementPrintCount, deleteFromHistory, fetchHistory, loading: historyLoading } = useBarcodeHistory();
  const { success } = useNotification();

  const adminId = getAdminId(user);

  const [activeTab, setActiveTab] = useState<TabType>('generate');
  const [codeType, setCodeType] = useState<BarcodeType>('upc-a');
  const [generatedCode, setGeneratedCode] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [useCustomCode, setUseCustomCode] = useState(false);
  const [codeValidation, setCodeValidation] = useState<{ valid: boolean; message: string } | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [barcodeFilter, setBarcodeFilter] = useState<BarcodeFilter>('all');
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const [bulkSortColumn, setBulkSortColumn] = useState<'name' | 'category' | 'binLocation' | 'sku'>('name');
  const [bulkSortDirection, setBulkSortDirection] = useState<'asc' | 'desc'>('asc');
  const [bulkBinLocationsMap, setBulkBinLocationsMap] = useState<Map<string, ProductBinLocation>>(new Map());
  const [bulkBinLocationsLoading, setBulkBinLocationsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    quantity: '',
    location: '',
    buy_price: '',
    sell_price: '',
    danger_category: ''
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [processingBatch, setProcessingBatch] = useState(false);
  const [generatedBarcodes, setGeneratedBarcodes] = useState<Array<{
    itemId: string;
    itemName: string;
    category: string;
    batchNumber: string;
    barcode: string;
    codeType: BarcodeType;
    location?: string;
    binLocation?: ProductBinLocation;
    price?: number;
  }>>([]);

  const [selectedBinDetails, setSelectedBinDetails] = useState<BinDetails | null>(null);

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateCodeType, setGenerateCodeType] = useState<BarcodeType>('upc-a');
  const [generatingBarcodes, setGeneratingBarcodes] = useState(false);

  const [selectedTemplate, setSelectedTemplate] = useState<LabelTemplate>(DEFAULT_TEMPLATES[1]);
  const [customTemplate, setCustomTemplate] = useState<LabelTemplate>({
    ...DEFAULT_TEMPLATES[1],
    id: 'custom',
    name: 'Custom',
    locationPosition: 'below',
    textStyle: 'normal',
    textColor: '#111111',
  });

  const [historySearch, setHistorySearch] = useState('');
  const [historyCodeType, setHistoryCodeType] = useState<BarcodeType | ''>('');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');

  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [selectedBarcodesToPrint, setSelectedBarcodesToPrint] = useState<Set<string>>(new Set());
  const [labelsPerPage, setLabelsPerPage] = useState<1 | 2 | 4 | 6 | 9>(4);

  const [loading, setLoading] = useState(false);
  const [showStatsPanel, setShowStatsPanel] = useState(true);

  const { settings: phomemoSettings, effectiveLabelSpec } = usePhomemoSettings();
  const [phomemoPrinting, setPhomemoPrinting] = useState(false);
  const [phomemoProgress, setPhomemoProgress] = useState<{ chunk: number; total: number } | null>(null);
  const phomemoAbortRef = useRef<AbortController | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const canModify = user?.role !== 'viewer';

  const currentCodeValue = useCustomCode ? customCode : generatedCode;

  useEffect(() => {
    if (currentCodeValue && canvasRef.current) {
      drawBarcodeToCanvas(canvasRef.current, currentCodeValue, codeType, {
        barWidth: 2,
        barHeight: 80,
        margin: 10,
        showText: true,
        fontSize: 12
      });
    }
  }, [currentCodeValue, codeType]);

  useEffect(() => {
    if (useCustomCode && customCode) {
      setCodeValidation(validateBarcode(customCode, codeType));
    } else {
      setCodeValidation(null);
    }
  }, [customCode, codeType, useCustomCode]);

  useEffect(() => {
    if (activeTab === 'bulk' && !itemsLoaded) {
      fetchAllItems().then(() => setItemsLoaded(true));
    }
  }, [activeTab, itemsLoaded, fetchAllItems]);

  const printBarcode = useCallback(async () => {
    if (!currentCodeValue) return;

    const binLocationData: ProductBinLocation | undefined = selectedBinDetails
      ? {
          warehouse_id: selectedBinDetails.warehouseId,
          warehouse_name: selectedBinDetails.warehouseName,
          zone_id: selectedBinDetails.zoneId || null,
          zone_code: selectedBinDetails.zoneCode || null,
          rack_id: selectedBinDetails.rackId,
          rack_code: selectedBinDetails.rackCode,
          bin_id: selectedBinDetails.binId,
          bin_code: selectedBinDetails.binCode,
          full_address: selectedBinDetails.fullPath,
          short_code: '',
          bin_inventory_id: '',
          quantity: 0
        }
      : undefined;

    const labelData: LabelData = {
      barcode: currentCodeValue,
      codeType: codeType,
      name: formData.name.trim() || undefined,
      price: parseFloat(formData.sell_price) || undefined,
      sku: currentCodeValue,
      location: formData.location.trim() || undefined,
      binLocation: binLocationData,
      category: formData.category.trim() || undefined,
      date: new Date()
    };

    const html = generateSingleLabelPrintHTML(labelData, selectedTemplate, {
      barWidth: 3,
      barHeight: 100,
      margin: 20
    });

    if (!html) {
      alert('Failed to generate label');
      return;
    }

    const barcodeDataUrl = getBarcodeDataURL(currentCodeValue, codeType);
    const ac = new AbortController();
    phomemoAbortRef.current = ac;
    setPhomemoPrinting(true);
    setPhomemoProgress(null);
    try {
      await openPrintWindowOrPhomemo(html, {
        usePhomemo: true,
        labelPdfData: {
          barcodeDataUrl: barcodeDataUrl || undefined,
          title: formData.name?.trim() || undefined,
          sku: currentCodeValue,
          location: formatLocationForLabel(formData.location?.trim(), binLocationData) || undefined
        },
        labelSpec: effectiveLabelSpec,
        phomemoDensity: phomemoSettings.density,
        phomemoBarcodeSafeMode: phomemoSettings.barcodeSafeMode,
        onProgress: (chunk, total) => setPhomemoProgress({ chunk, total }),
        signal: ac.signal,
      });
    } finally {
      setPhomemoPrinting(false);
      setPhomemoProgress(null);
      phomemoAbortRef.current = null;
    }
  }, [currentCodeValue, codeType, formData, selectedTemplate, selectedBinDetails, phomemoSettings.density, phomemoSettings.barcodeSafeMode, effectiveLabelSpec]);

  const handleGenerateCode = () => {
    const newCode = generateBarcode(codeType);
    setGeneratedCode(newCode);
    setUseCustomCode(false);
    setFormData(prev => ({ ...prev, name: prev.name || `Item ${newCode.slice(-6)}` }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.category.trim()) {
      alert(t('common.required'));
      return;
    }

    if (!currentCodeValue) {
      alert('Please generate a barcode first');
      return;
    }

    if (useCustomCode && codeValidation && !codeValidation.valid) {
      alert(codeValidation.message);
      return;
    }

    setLoading(true);

    const itemData = {
      name: formData.name.trim(),
      category: formData.category.trim(),
      quantity: parseInt(formData.quantity) || 0,
      location: formData.location.trim() || undefined,
      sku: currentCodeValue,
      barcode: currentCodeValue,
      batch: currentCodeValue,
      buy_price: parseFloat(formData.buy_price) || undefined,
      sell_price: parseFloat(formData.sell_price) || undefined,
      unit_price: parseFloat(formData.sell_price) || parseFloat(formData.buy_price) || 0,
      danger_category: formData.danger_category.trim() || undefined,
    };

    try {
      const result = await addItem(itemData);

      if (result) {
        const locationForHistory = selectedBinDetails
          ? formatLocationForLabel(formData.location.trim(), {
              warehouse_id: selectedBinDetails.warehouseId,
              warehouse_name: selectedBinDetails.warehouseName,
              zone_id: selectedBinDetails.zoneId || null,
              zone_code: selectedBinDetails.zoneCode || null,
              rack_id: selectedBinDetails.rackId,
              rack_code: selectedBinDetails.rackCode,
              bin_id: selectedBinDetails.binId,
              bin_code: selectedBinDetails.binCode,
              full_address: selectedBinDetails.fullPath,
              short_code: '',
              bin_inventory_id: '',
              quantity: 0
            })
          : formData.location.trim();

        await addToHistory({
          product_id: result.id,
          barcode_value: currentCodeValue,
          code_type: codeType,
          code_format: codeType === 'qrcode' || codeType === 'datamatrix' ? 'qrcode' : 'barcode',
          product_name: formData.name.trim(),
          category: formData.category.trim(),
          location: locationForHistory || undefined,
          bin_id: selectedBinDetails?.binId,
          label_template: selectedTemplate.id
        });

        alert(t('barcode.createdSuccess'));
        setFormData({
          name: '',
          category: '',
          quantity: '',
          location: '',
          buy_price: '',
          sell_price: '',
          danger_category: ''
        });
        setGeneratedCode('');
        setCustomCode('');
        setSelectedBinDetails(null);
      } else {
        alert(t('common.failed'));
      }
    } catch (error) {
      console.error('Error creating item:', error);
      alert(t('common.failed'));
    } finally {
      setLoading(false);
    }
  };

  const filteredExistingItems = allItems.filter(item => {
    const matchesSearch = searchTerm === '' ||
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.barcode?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesBarcodeFilter =
      barcodeFilter === 'all' ? true :
      barcodeFilter === 'has_barcode' ? !!item.barcode :
      barcodeFilter === 'no_barcode' ? !item.barcode : true;

    return matchesSearch && matchesBarcodeFilter;
  });

  const sortedFilteredItems = React.useMemo(() => {
    const sorted = [...filteredExistingItems];
    const cmp = (a: string, b: string) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });
    sorted.sort((a, b) => {
      let aVal: string;
      let bVal: string;
      switch (bulkSortColumn) {
        case 'name':
          aVal = a.name;
          bVal = b.name;
          break;
        case 'category':
          aVal = a.category;
          bVal = b.category;
          break;
        case 'sku':
          aVal = a.sku || '';
          bVal = b.sku || '';
          break;
        case 'binLocation':
          aVal = formatBinLocationCompact(bulkBinLocationsMap.get(a.id)) || '';
          bVal = formatBinLocationCompact(bulkBinLocationsMap.get(b.id)) || '';
          break;
        default:
          return 0;
      }
      const result = cmp(aVal, bVal);
      return bulkSortDirection === 'asc' ? result : -result;
    });
    return sorted;
  }, [filteredExistingItems, bulkSortColumn, bulkSortDirection, bulkBinLocationsMap]);

  const filteredItemIds = filteredExistingItems.map(i => i.id).join(',');
  useEffect(() => {
    if (activeTab !== 'bulk' || filteredItemIds === '') {
      setBulkBinLocationsMap(new Map());
      return;
    }
    const productIds = filteredItemIds.split(',').filter(Boolean);
    setBulkBinLocationsLoading(true);
    getProductsBinLocations(productIds, adminId)
      .then(map => setBulkBinLocationsMap(map))
      .finally(() => setBulkBinLocationsLoading(false));
  }, [activeTab, filteredItemIds, adminId]);

  const totalPages = Math.ceil(sortedFilteredItems.length / ITEMS_PER_PAGE);
  const paginatedItems = sortedFilteredItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const itemsWithBarcode = filteredExistingItems.filter(item => !!item.barcode).length;
  const itemsWithoutBarcode = filteredExistingItems.filter(item => !item.barcode).length;

  const toggleItemSelection = (itemId: string) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(itemId)) {
      newSelection.delete(itemId);
    } else {
      newSelection.add(itemId);
    }
    setSelectedItems(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === paginatedItems.length && paginatedItems.length > 0) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(paginatedItems.map(item => item.id)));
    }
  };

  const selectAllFiltered = () => {
    setSelectedItems(new Set(filteredExistingItems.map(item => item.id)));
  };

  const handleExportExcel = async () => {
    const productIds = sortedFilteredItems.map(i => i.id);
    const binMap = productIds.length > 0 ? await getProductsBinLocations(productIds, adminId) : new Map<string, ProductBinLocation>();
    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Bulk Barcode Labels');
    ws.columns = [
      { key: 'name', width: 30 },
      { key: 'category', width: 18 },
      { key: 'sku', width: 18 },
      { key: 'binLocation', width: 25 },
      { key: 'barcode', width: 20 },
      { key: 'hasCode', width: 10 },
    ];
    ws.addRow(['Name', 'Category', 'SKU', 'Bin Location', 'Barcode', 'Has Code']);
    sortedFilteredItems.forEach(item => {
      const binLoc = binMap.get(item.id);
      const binStr = formatBinLocationCompact(binLoc) || '-';
      ws.addRow([item.name, item.category, item.sku || '-', binStr, item.barcode || '-', item.barcode ? 'Yes' : 'No']);
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk_barcode_labels_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
    success('Exported to Excel');
  };

  const handlePrepareLabelsForSelected = async () => {
    if (selectedItems.size === 0 || !user) return;

    setProcessingBatch(true);
    const prepared: typeof generatedBarcodes = [];

    try {
      const itemIds = Array.from(selectedItems);
      const binLocations = await getProductsBinLocations(itemIds, adminId);

      for (const itemId of itemIds) {
        const item = allItems.find(i => i.id === itemId);
        if (!item) continue;

        if (item.barcode) {
          const binLocation = binLocations.get(item.id);
          prepared.push({
            itemId: item.id,
            itemName: item.name,
            category: item.category,
            batchNumber: item.batch_number || '',
            barcode: item.barcode,
            codeType: codeType,
            location: item.location || '',
            binLocation: binLocation || undefined,
            price: item.sell_price || item.unit_price || 0
          });
        }
      }

      setGeneratedBarcodes(prepared);
      setSelectedItems(new Set());

      if (prepared.length === 0) {
        alert('No items with barcodes were selected. Select items that already have barcodes to print labels.');
      } else {
        alert(`${prepared.length} barcode label(s) ready to print!`);
      }
    } catch (error) {
      console.error('Error generating barcodes:', error);
      alert('Failed to generate barcodes. Please try again.');
    } finally {
      setProcessingBatch(false);
    }
  };

  const handleBulkGenerateBarcodes = async () => {
    if (selectedItems.size === 0 || !user) return;

    const itemsToGenerate = Array.from(selectedItems)
      .map(id => allItems.find(i => i.id === id))
      .filter(item => item && !item.barcode);

    if (itemsToGenerate.length === 0) {
      alert('All selected items already have barcodes. No new barcodes to generate.');
      setShowGenerateModal(false);
      return;
    }

    setGeneratingBarcodes(true);

    try {
      const successIds: string[] = [];
      const failedIds: string[] = [];
      const newBarcodes: typeof generatedBarcodes = [];

      for (const item of itemsToGenerate) {
        if (!item) continue;

        const newBarcode = generateBarcode(generateCodeType);

        const result = await updateMultipleItems([item.id], {
          barcode: newBarcode,
          batch_number: newBarcode
        });

        if (result.success.length > 0) {
          successIds.push(item.id);
          newBarcodes.push({
            itemId: item.id,
            itemName: item.name,
            category: item.category,
            batchNumber: newBarcode,
            barcode: newBarcode,
            codeType: generateCodeType,
            location: item.location || '',
            price: item.sell_price || item.unit_price || 0
          });

          await addToHistory({
            product_id: item.id,
            barcode_value: newBarcode,
            code_type: generateCodeType,
            code_format: generateCodeType === 'qrcode' || generateCodeType === 'datamatrix' ? 'qrcode' : 'barcode',
            product_name: item.name,
            category: item.category,
            label_template: selectedTemplate.id
          });
        } else {
          failedIds.push(item.id);
        }
      }

      setGeneratedBarcodes(prev => [...prev, ...newBarcodes]);
      setSelectedItems(new Set());
      setShowGenerateModal(false);

      await fetchAllItems(true);

      if (successIds.length > 0) {
        alert(`Successfully generated ${successIds.length} barcode(s)!${failedIds.length > 0 ? ` ${failedIds.length} failed.` : ''}`);
      } else {
        alert('Failed to generate barcodes. Please try again.');
      }
    } catch (error) {
      console.error('Error generating barcodes:', error);
      alert('Failed to generate barcodes. Please try again.');
    } finally {
      setGeneratingBarcodes(false);
    }
  };

  const selectedItemsWithoutBarcode = Array.from(selectedItems)
    .map(id => allItems.find(i => i.id === id))
    .filter(item => item && !item.barcode).length;

  const downloadBarcode = () => {
    if (!currentCodeValue) return;
    const dataUrl = getBarcodeDataURL(currentCodeValue, codeType, { barWidth: 3, barHeight: 120, margin: 30 });
    if (!dataUrl) return;

    const link = document.createElement('a');
    link.download = `${codeType}-${currentCodeValue}.png`;
    link.href = dataUrl;
    link.click();
  };

  const handleHistoryFilter = () => {
    fetchHistory({
      search: historySearch || undefined,
      codeType: historyCodeType || undefined,
      dateFrom: historyDateFrom || undefined,
      dateTo: historyDateTo || undefined,
      limit: 50
    });
  };

  const handleReprintFromHistory = async (record: typeof history[0]) => {
    incrementPrintCount(record.id);

    const labelData: LabelData = {
      barcode: record.barcode_value,
      codeType: record.code_type,
      name: record.product_name,
      sku: record.barcode_value,
      location: record.location || undefined,
      category: record.category || undefined,
      date: new Date(record.created_at)
    };

    const html = generateSingleLabelPrintHTML(labelData, selectedTemplate, {
      barWidth: 3,
      barHeight: 100,
      margin: 20
    });

    if (!html) {
      alert('Failed to generate label');
      return;
    }

    const barcodeDataUrl = getBarcodeDataURL(record.barcode_value, record.code_type);
    const ac = new AbortController();
    phomemoAbortRef.current = ac;
    setPhomemoPrinting(true);
    setPhomemoProgress(null);
    try {
      await openPrintWindowOrPhomemo(html, {
        usePhomemo: true,
        labelPdfData: {
          barcodeDataUrl: barcodeDataUrl || undefined,
          title: record.product_name || undefined,
          sku: record.barcode_value,
          location: record.location || undefined
        },
        labelSpec: effectiveLabelSpec,
        phomemoDensity: phomemoSettings.density,
        phomemoBarcodeSafeMode: phomemoSettings.barcodeSafeMode,
        onProgress: (chunk, total) => setPhomemoProgress({ chunk, total }),
        signal: ac.signal,
      });
    } finally {
      setPhomemoPrinting(false);
      setPhomemoProgress(null);
      phomemoAbortRef.current = null;
    }
  };

  const openBulkPrintPreview = () => {
    setSelectedBarcodesToPrint(new Set(generatedBarcodes.map(b => b.itemId)));
    setShowPrintPreview(true);
  };

  const handleBulkPrint = () => {
    const toPrint = generatedBarcodes.filter(b => selectedBarcodesToPrint.has(b.itemId));
    if (toPrint.length === 0) return;

    const labelDataArray: LabelData[] = toPrint.map(item => ({
      barcode: item.barcode,
      codeType: item.codeType,
      name: item.itemName,
      price: item.price,
      sku: item.barcode,
      location: item.location,
      binLocation: item.binLocation,
      category: item.category,
      date: new Date()
    }));

    const html = generateBulkLabelsPrintHTML(labelDataArray, selectedTemplate, labelsPerPage, {
      barWidth: 3,
      barHeight: 100,
      margin: 20
    });

    if (!html) {
      alert('No valid barcodes to print');
      return;
    }

    if (!openPrintWindow(html)) {
      alert('Please allow pop-ups');
    }

    setShowPrintPreview(false);
  };

  const codeFormat: CodeFormat = codeType === 'qrcode' || codeType === 'datamatrix' ? 'qrcode' : 'barcode';

  return (
    <div className={`p-4 sm:p-6 mx-auto ${activeTab === 'rfid' || activeTab === 'rfid-encoder' ? 'max-w-[1800px] w-full' : 'max-w-7xl'}`}>
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center">
            <QrCode className="h-8 w-8 text-teal-500 mr-3" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
              {t('barcode.title')}
            </h1>
          </div>
        </div>

        {showStatsPanel && stats.totalGenerated > 0 && (
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-xl p-4 mb-6 border border-teal-100 dark:border-teal-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-teal-800 dark:text-teal-200 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Generation Statistics
              </h3>
              <button
                onClick={() => setShowStatsPanel(false)}
                className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalGenerated}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Total Generated</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.thisMonth}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">This Month</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
                <div className="text-lg font-bold text-gray-900 dark:text-gray-100 uppercase">{stats.mostUsedFormat || '-'}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Most Used Format</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{history.length}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">In History</div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
          {[
            { id: 'generate' as TabType, label: 'Generate New', icon: PlusCircle },
            { id: 'bulk' as TabType, label: 'Bulk Generate', icon: Layers },
            { id: 'templates' as TabType, label: 'Label Templates', icon: LayoutTemplate },
            { id: 'history' as TabType, label: 'History', icon: History },
            { id: 'rfid' as TabType, label: 'RFID Item Identifier', icon: Tag },
            { id: 'rfid-encoder' as TabType, label: 'RFID Encoder', icon: Radio }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-[120px] px-3 py-2 rounded-md flex items-center justify-center gap-2 text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-700 shadow-sm text-teal-600 dark:text-teal-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'generate' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Code Generator
            </h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Code Format
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {BARCODE_FORMATS.map(format => (
                  <button
                    key={format.type}
                    onClick={() => {
                      setCodeType(format.type);
                      setGeneratedCode('');
                      setCustomCode('');
                    }}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      codeType === format.type
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {format.format === 'barcode' ? (
                        <Barcode className="h-4 w-4 text-gray-500" />
                      ) : (
                        <QrCode className="h-4 w-4 text-gray-500" />
                      )}
                      <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{format.name}</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{format.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={useCustomCode}
                  onChange={(e) => setUseCustomCode(e.target.checked)}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Enter custom code value</span>
              </label>
              {useCustomCode && (
                <div>
                  <Input
                    value={customCode}
                    onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                    placeholder={BARCODE_FORMATS.find(f => f.type === codeType)?.example || ''}
                    className="font-mono"
                  />
                  {codeValidation && (
                    <p className={`mt-1 text-xs ${codeValidation.valid ? 'text-green-600' : 'text-red-600'}`}>
                      {codeValidation.message}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
              {currentCodeValue ? (
                <div className="text-center">
                  <div className="text-xl font-mono font-bold text-gray-900 dark:text-gray-100 mb-3">
                    {currentCodeValue}
                  </div>
                  <div className="flex justify-center mb-3">
                    <canvas ref={canvasRef} className="border border-gray-200 dark:border-gray-700 rounded bg-white" />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {BARCODE_FORMATS.find(f => f.type === codeType)?.name} | Generated {new Date().toLocaleDateString()}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  {codeFormat === 'barcode' ? (
                    <Barcode className="h-16 w-16 text-gray-300 mx-auto mb-3" />
                  ) : (
                    <QrCode className="h-16 w-16 text-gray-300 mx-auto mb-3" />
                  )}
                  <p className="text-gray-400 text-sm">
                    {useCustomCode ? 'Enter a custom code above' : 'Click Generate to create a new code'}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {!useCustomCode && (
                <Button onClick={handleGenerateCode} variant="primary" className="flex-1" disabled={!canModify}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Generate
                </Button>
              )}
              {currentCodeValue && (
                <>
                  <Button onClick={downloadBarcode} variant="secondary">
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button onClick={printBarcode} variant="secondary">
                    <Printer className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Create Item with Code
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label={t('inventory.itemName') + ' *'}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('inventory.enterItemName')}
                required
                disabled={!canModify}
              />

              <Input
                label={t('product.category') + ' *'}
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder={t('inventory.enterCategory')}
                required
                disabled={!canModify}
              />

              <Input
                label={t('product.quantity')}
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                placeholder="0"
                min="0"
                disabled={!canModify}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('inventory.table.location')}
                </label>
                <LocationPicker
                  value={formData.location}
                  onChange={(location, binDetails) => {
                    setFormData({ ...formData, location });
                    setSelectedBinDetails(binDetails || null);
                  }}
                  placeholder="Select bin location..."
                  disabled={!canModify}
                />
                {selectedBinDetails && (
                  <p className="mt-1 text-xs text-teal-600 dark:text-teal-400 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {selectedBinDetails.zoneCode || ''}{selectedBinDetails.rackCode?.replace('RACK-', 'R') || ''}{selectedBinDetails.binCode ? `L${selectedBinDetails.binCode.split('-').pop()?.replace(/[A-Z]/g, '') || ''}` : ''}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label={t('common.buyPrice')}
                  type="number"
                  step="0.01"
                  value={formData.buy_price}
                  onChange={(e) => setFormData({ ...formData, buy_price: e.target.value })}
                  placeholder="0.00"
                  min="0"
                  disabled={!canModify}
                />
                <Input
                  label={t('common.sellPrice')}
                  type="number"
                  step="0.01"
                  value={formData.sell_price}
                  onChange={(e) => setFormData({ ...formData, sell_price: e.target.value })}
                  placeholder="0.00"
                  min="0"
                  disabled={!canModify}
                />
              </div>

              {currentCodeValue && (
                <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-800">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-teal-600" />
                    <span className="text-sm font-medium text-teal-800 dark:text-teal-200">
                      Code Ready: {currentCodeValue}
                    </span>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                loading={loading}
                disabled={!canModify || !currentCodeValue || (useCustomCode && codeValidation && !codeValidation.valid)}
              >
                <Package className="h-4 w-4 mr-2" />
                Create Item
              </Button>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'bulk' && (
        <div className="card p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Bulk Barcode Labels
            </h2>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-500">
                {selectedItems.size} selected
              </span>
              <span className="text-green-600 dark:text-green-400">
                {itemsWithBarcode} with barcode
              </span>
              <span className="text-gray-400">
                {itemsWithoutBarcode} without
              </span>
            </div>
          </div>

          {allItemsLoading && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 text-teal-500 animate-spin mr-2" />
              <span className="text-gray-500">Loading inventory...</span>
            </div>
          )}

          {!allItemsLoading && (
            <>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, SKU, or barcode..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <select
                  value={barcodeFilter}
                  onChange={(e) => {
                    setBarcodeFilter(e.target.value as BarcodeFilter);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Items</option>
                  <option value="has_barcode">Has Barcode</option>
                  <option value="no_barcode">No Barcode</option>
                </select>
                <select
                  value={codeType}
                  onChange={(e) => setCodeType(e.target.value as BarcodeType)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  {BARCODE_FORMATS.map(format => (
                    <option key={format.type} value={format.type}>{format.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap items-center gap-2 py-2 mb-2 text-sm text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <span className="font-medium">Sort by:</span>
                {(['name', 'category', 'binLocation', 'sku'] as const).map(col => {
                  const label = col === 'binLocation' ? 'Bin Location' : col === 'sku' ? 'SKU' : col.charAt(0).toUpperCase() + col.slice(1);
                  const isActive = bulkSortColumn === col;
                  return (
                    <button
                      key={col}
                      type="button"
                      onClick={() => {
                        const isSameColumn = bulkSortColumn === col;
                        setBulkSortColumn(col);
                        setBulkSortDirection(isSameColumn ? (bulkSortDirection === 'asc' ? 'desc' : 'asc') : 'asc');
                        setCurrentPage(1);
                      }}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                        isActive
                          ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      {label}
                      {isActive && (bulkSortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <Button
                  onClick={() => setShowGenerateModal(true)}
                  variant="primary"
                  disabled={!canModify || selectedItems.size === 0 || selectedItemsWithoutBarcode === 0}
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  Generate ({selectedItemsWithoutBarcode})
                </Button>
                <Button
                  onClick={handlePrepareLabelsForSelected}
                  variant="secondary"
                  disabled={!canModify || selectedItems.size === 0 || processingBatch}
                  loading={processingBatch}
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Prepare Labels ({selectedItems.size})
                </Button>
                {generatedBarcodes.length > 0 && (
                  <Button onClick={openBulkPrintPreview} variant="secondary">
                    <Printer className="h-4 w-4 mr-2" />
                    Print ({generatedBarcodes.length})
                  </Button>
                )}
                {filteredExistingItems.length > ITEMS_PER_PAGE && (
                  <Button
                    onClick={selectAllFiltered}
                    variant="secondary"
                  >
                    Select All ({filteredExistingItems.length})
                  </Button>
                )}
                <Button
                  onClick={() => setSelectedItems(new Set())}
                  variant="secondary"
                  disabled={selectedItems.size === 0}
                >
                  Clear Selection
                </Button>
                <Button
                  onClick={handleExportExcel}
                  variant="secondary"
                  disabled={sortedFilteredItems.length === 0}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export Excel
                </Button>
              </div>

              <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedItems.size === paginatedItems.length && paginatedItems.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">SKU</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Bin Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden sm:table-cell">Barcode</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Has Code</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedItems.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                          {allItems.length === 0 ? 'No items in inventory' : 'No items match your filters'}
                        </td>
                      </tr>
                    ) : (
                      paginatedItems.map(item => (
                        <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(item.id)}
                              onChange={() => toggleItemSelection(item.id)}
                              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{item.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.category}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            {item.sku ? (
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(item.sku || '');
                                  success('SKU copied to clipboard');
                                }}
                                className="font-mono text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 hover:underline cursor-pointer"
                                title="Click to copy"
                              >
                                {item.sku}
                              </button>
                            ) : (
                              <span className="text-gray-400 italic">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                            {bulkBinLocationsLoading ? (
                              <span className="text-gray-400">...</span>
                            ) : (
                              (() => {
                                const binLoc = bulkBinLocationsMap.get(item.id);
                                return binLoc ? (
                                  <span className="text-xs" title={binLoc.full_address}>
                                    {formatBinLocationCompact(binLoc)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 italic">-</span>
                                );
                              })()
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-300 hidden sm:table-cell">
                            {item.barcode || <span className="text-gray-400 italic">-</span>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {item.barcode ? (
                              <Check className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="h-5 w-5 text-gray-300 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-sm text-gray-500">
                    Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredExistingItems.length)} of {filteredExistingItems.length} items
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                              currentPage === pageNum
                                ? 'bg-teal-500 text-white'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {generatedBarcodes.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Ready to Print ({generatedBarcodes.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {generatedBarcodes.slice(0, 6).map(item => (
                      <div key={item.itemId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{item.itemName}</div>
                        <div className="text-xs font-mono text-gray-500 mt-1">{item.barcode}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Label Presets
            </h2>

            <div className="space-y-3">
              {DEFAULT_TEMPLATES.map(template => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    selectedTemplate.id === template.id
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{template.name}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        {template.width}" x {template.height}" | {template.fontSize} text
                      </div>
                    </div>
                    {selectedTemplate.id === template.id && (
                      <Check className="h-5 w-5 text-teal-500" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Custom Settings
              </h3>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Width (inches)</label>
                  <input
                    type="number"
                    step="0.25"
                    value={customTemplate.width}
                    onChange={(e) => {
                      const newTemplate = { ...customTemplate, width: parseFloat(e.target.value) || 2 };
                      setCustomTemplate(newTemplate);
                      setSelectedTemplate(newTemplate);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Height (inches)</label>
                  <input
                    type="number"
                    step="0.25"
                    value={customTemplate.height}
                    onChange={(e) => {
                      const newTemplate = { ...customTemplate, height: parseFloat(e.target.value) || 1 };
                      setCustomTemplate(newTemplate);
                      setSelectedTemplate(newTemplate);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                  />
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {[
                  { key: 'showName', label: 'Show Product Name' },
                  { key: 'showPrice', label: 'Show Price' },
                  { key: 'showSku', label: 'Show SKU' },
                  { key: 'showDate', label: 'Show Date' },
                  { key: 'showLocation', label: 'Show Location' }
                ].map(option => (
                  <label key={option.key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedTemplate[option.key as keyof LabelTemplate] as boolean}
                      onChange={(e) => {
                        const newTemplate = { ...selectedTemplate, [option.key]: e.target.checked, id: 'custom', name: 'Custom' };
                        setCustomTemplate(newTemplate);
                        setSelectedTemplate(newTemplate);
                      }}
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                  </label>
                ))}
              </div>

              {selectedTemplate.showLocation && (
                <div className="mt-4">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Location position</label>
                  <select
                    value={selectedTemplate.locationPosition ?? 'below'}
                    onChange={(e) => {
                      const newTemplate = { ...selectedTemplate, locationPosition: e.target.value as 'above' | 'below', id: 'custom', name: 'Custom' };
                      setCustomTemplate(newTemplate);
                      setSelectedTemplate(newTemplate);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                  >
                    <option value="above">Above barcode</option>
                    <option value="below">Below barcode</option>
                  </select>
                </div>
              )}

              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Text style</label>
                <select
                  value={selectedTemplate.textStyle ?? 'normal'}
                  onChange={(e) => {
                    const newTemplate = { ...selectedTemplate, textStyle: e.target.value as 'normal' | 'bold' | 'italic', id: 'custom', name: 'Custom' };
                    setCustomTemplate(newTemplate);
                    setSelectedTemplate(newTemplate);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                >
                  <option value="normal">Normal</option>
                  <option value="bold">Bold</option>
                  <option value="italic">Italic</option>
                </select>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Text color</label>
                <input
                  type="color"
                  value={selectedTemplate.textColor ?? '#111111'}
                  onChange={(e) => {
                    const newTemplate = { ...selectedTemplate, textColor: e.target.value, id: 'custom', name: 'Custom' };
                    setCustomTemplate(newTemplate);
                    setSelectedTemplate(newTemplate);
                  }}
                  className="h-9 w-14 cursor-pointer rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                />
                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-mono">{selectedTemplate.textColor ?? '#111111'}</span>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Letter size</label>
                <select
                  value={selectedTemplate.fontSize}
                  onChange={(e) => {
                    const newTemplate = { ...selectedTemplate, fontSize: e.target.value as 'small' | 'medium' | 'large', id: 'custom', name: 'Custom' };
                    setCustomTemplate(newTemplate);
                    setSelectedTemplate(newTemplate);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                >
                  <option value="small">Small (10px)</option>
                  <option value="medium">Medium (12px)</option>
                  <option value="large">Large (14px)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Label Preview
            </h2>

            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-8 flex items-center justify-center min-h-[300px]">
              {(() => {
                const locPos = selectedTemplate.locationPosition ?? 'below';
                const textStyle = selectedTemplate.textStyle ?? 'normal';
                const textColor = selectedTemplate.textColor ?? '#111111';
                const sizeCls = selectedTemplate.fontSize === 'small' ? 'text-xs' : selectedTemplate.fontSize === 'medium' ? 'text-sm' : 'text-base';
                const styleCls = textStyle === 'bold' ? 'font-bold' : textStyle === 'italic' ? 'italic' : '';
                const textStyleObj = { color: textColor } as React.CSSProperties;
                return (
              <div
                className="bg-white border-2 border-dashed border-gray-300 rounded shadow-sm p-4 flex flex-col items-center justify-center"
                style={{
                  width: `${Math.min(selectedTemplate.width * 96, 350)}px`,
                  minHeight: `${Math.min(selectedTemplate.height * 96, 200)}px`
                }}
              >
                {selectedTemplate.showName && (
                  <div className={`mb-2 text-center ${sizeCls} ${styleCls}`} style={textStyleObj}>
                    Sample Product Name
                  </div>
                )}

                {selectedTemplate.showLocation && locPos === 'above' && (
                  <div className={`text-teal-600 font-medium mb-1 ${sizeCls} ${styleCls}`}>Z1R01L2</div>
                )}

                <div className="flex-1 flex items-center justify-center my-2">
                  <Barcode className="h-12 w-24 text-gray-800" />
                </div>

                <div className={`text-xs font-mono mb-1 ${styleCls}`} style={textStyleObj}>012345678905</div>

                {selectedTemplate.showPrice && (
                  <div className={`${sizeCls} ${styleCls}`} style={textStyleObj}>$9.99</div>
                )}

                {selectedTemplate.showSku && (
                  <div className={`${sizeCls} mt-1 ${styleCls}`} style={textStyleObj}>SKU: ABC-123</div>
                )}

                {selectedTemplate.showLocation && locPos === 'below' && (
                  <div className={`text-teal-600 font-medium mt-1 ${sizeCls} ${styleCls}`}>Z1R01L2</div>
                )}

                {selectedTemplate.showDate && (
                  <div className={`${sizeCls} mt-1 ${styleCls}`} style={textStyleObj}>{new Date().toLocaleDateString()}</div>
                )}
              </div>
                );
              })()}
            </div>

            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                <strong>Template:</strong> {selectedTemplate.name}
                <br />
                <strong>Size:</strong> {selectedTemplate.width}" x {selectedTemplate.height}"
                <br />
                <strong>Elements:</strong> {[
                  selectedTemplate.showName && 'Name',
                  selectedTemplate.showPrice && 'Price',
                  selectedTemplate.showSku && 'SKU',
                  selectedTemplate.showDate && 'Date',
                  selectedTemplate.showLocation && 'Location'
                ].filter(Boolean).join(', ') || 'None'}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'rfid' && <RfidIdentifierTab />}

      {activeTab === 'rfid-encoder' && <RfidEncoderTab />}

      {activeTab === 'history' && (
        <div className="card p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Barcode History
            </h2>
            <Button onClick={() => fetchHistory({ limit: 50 })} variant="secondary" loading={historyLoading}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by code or product name..."
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
            <select
              value={historyCodeType}
              onChange={(e) => setHistoryCodeType(e.target.value as BarcodeType | '')}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            >
              <option value="">All Formats</option>
              {BARCODE_FORMATS.map(format => (
                <option key={format.type} value={format.type}>{format.name}</option>
              ))}
            </select>
            <input
              type="date"
              value={historyDateFrom}
              onChange={(e) => setHistoryDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
              placeholder="From"
            />
            <input
              type="date"
              value={historyDateTo}
              onChange={(e) => setHistoryDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
              placeholder="To"
            />
            <Button onClick={handleHistoryFilter} variant="secondary">
              <Filter className="h-4 w-4" />
            </Button>
          </div>

          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preview</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Date</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Prints</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      {historyLoading ? 'Loading...' : 'No barcode history found'}
                    </td>
                  </tr>
                ) : (
                  history.map(record => (
                    <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-4 py-3">
                        <div className="w-16 h-10 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
                          {record.code_format === 'barcode' ? (
                            <Barcode className="h-6 w-12 text-gray-600" />
                          ) : (
                            <QrCode className="h-6 w-6 text-gray-600" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{record.product_name}</div>
                        <div className="text-xs text-gray-500">{record.category}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-gray-600 dark:text-gray-300 hidden sm:table-cell">
                        {record.barcode_value}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded uppercase">
                          {record.code_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell">
                        {new Date(record.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm text-gray-600 dark:text-gray-300">{record.print_count}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleReprintFromHistory(record)}
                            className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded transition-colors"
                            title="Print"
                          >
                            <Printer className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              const dataUrl = getBarcodeDataURL(record.barcode_value, record.code_type);
                              if (dataUrl) {
                                const link = document.createElement('a');
                                link.download = `${record.code_type}-${record.barcode_value}.png`;
                                link.href = dataUrl;
                                link.click();
                              }
                            }}
                            className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded transition-colors"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Delete barcode for "${record.product_name}"?`)) {
                                await deleteFromHistory(record.id);
                              }
                            }}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showPrintPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Print Preview</h2>
                <p className="text-sm text-gray-500">{selectedBarcodesToPrint.size} selected</p>
              </div>
              <button onClick={() => setShowPrintPreview(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedBarcodesToPrint.size === generatedBarcodes.length}
                  onChange={() => {
                    if (selectedBarcodesToPrint.size === generatedBarcodes.length) {
                      setSelectedBarcodesToPrint(new Set());
                    } else {
                      setSelectedBarcodesToPrint(new Set(generatedBarcodes.map(b => b.itemId)));
                    }
                  }}
                  className="rounded border-gray-300 text-teal-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Select All</span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Labels per page:</span>
                <select
                  value={labelsPerPage}
                  onChange={(e) => setLabelsPerPage(parseInt(e.target.value) as 1 | 2 | 4 | 6 | 9)}
                  className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800"
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={4}>4</option>
                  <option value={6}>6</option>
                  <option value={9}>9</option>
                </select>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {generatedBarcodes.map(item => (
                  <label
                    key={item.itemId}
                    className={`border rounded-lg p-3 cursor-pointer transition-all ${
                      selectedBarcodesToPrint.has(item.itemId)
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={selectedBarcodesToPrint.has(item.itemId)}
                        onChange={() => {
                          const newSet = new Set(selectedBarcodesToPrint);
                          if (newSet.has(item.itemId)) {
                            newSet.delete(item.itemId);
                          } else {
                            newSet.add(item.itemId);
                          }
                          setSelectedBarcodesToPrint(newSet);
                        }}
                        className="mt-1 rounded border-gray-300 text-teal-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{item.itemName}</div>
                        <div className="text-xs font-mono text-gray-500 mt-1">{item.barcode}</div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <Button onClick={() => setShowPrintPreview(false)} variant="secondary">
                Cancel
              </Button>
              <Button onClick={handleBulkPrint} variant="primary" disabled={selectedBarcodesToPrint.size === 0}>
                <Printer className="h-4 w-4 mr-2" />
                Print {selectedBarcodesToPrint.size}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <Wand2 className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Generate Barcodes</h2>
                  <p className="text-sm text-gray-500">{selectedItemsWithoutBarcode} item(s) without barcode</p>
                </div>
              </div>
              <button
                onClick={() => setShowGenerateModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Barcode Type
                </label>
                <select
                  value={generateCodeType}
                  onChange={(e) => setGenerateCodeType(e.target.value as BarcodeType)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  {BARCODE_FORMATS.map(format => (
                    <option key={format.type} value={format.type}>
                      {format.name} - {format.description}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Summary</h4>
                <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                  <div className="flex justify-between">
                    <span>Selected items:</span>
                    <span className="font-medium">{selectedItems.size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Already have barcode:</span>
                    <span className="font-medium text-gray-400">{selectedItems.size - selectedItemsWithoutBarcode} (skipped)</span>
                  </div>
                  <div className="flex justify-between text-teal-600 dark:text-teal-400">
                    <span>Will generate:</span>
                    <span className="font-bold">{selectedItemsWithoutBarcode}</span>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-6">
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Only items without existing barcodes will receive new codes. Existing barcodes are protected and will not be overwritten.
                </p>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-800/50">
              <Button
                onClick={() => setShowGenerateModal(false)}
                variant="secondary"
                disabled={generatingBarcodes}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkGenerateBarcodes}
                variant="primary"
                loading={generatingBarcodes}
                disabled={selectedItemsWithoutBarcode === 0 || generatingBarcodes}
              >
                <Wand2 className="h-4 w-4 mr-2" />
                Generate {selectedItemsWithoutBarcode} Barcode(s)
              </Button>
            </div>
          </div>
        </div>
      )}

      {phomemoPrinting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              {phomemoProgress ? (
                <Printer className="h-6 w-6 text-teal-500" />
              ) : (
                <RefreshCw className="h-6 w-6 text-teal-500 animate-spin" />
              )}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {phomemoProgress ? 'Printing to thermal printer…' : 'Preparing…'}
              </span>
            </div>
            {phomemoProgress && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Sending chunk {phomemoProgress.chunk} of {phomemoProgress.total}
                </p>
                <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                  <div
                    className="h-full bg-teal-500 rounded transition-all"
                    style={{ width: `${(100 * phomemoProgress.chunk) / phomemoProgress.total}%` }}
                  />
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => phomemoAbortRef.current?.abort()}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
